// Main navigation configuration, shared by the header and the section tabs.
//
// Every menu item is a section with one rule for what belongs in it:
// - Data: pages in this app that describe the data or deliver it
// - Tools: the search builder, and all tools that work with the data, from MatGUI to SPARQL
// - Data catalog: what the knowledge graph and the RINF export contain, a menu item of its own
// - Docs: API documentation, and the status of the DIM project, the RINF export and the railML
//   files
//
// The menu item opens a list of the section's pages, and each page of a section shows the
// section's pages as tabs. A section with one page is a plain link in the menu, without tabs.
// Breadcrumbs are only used on pages below a tab, starting from that tab.

import routes from '@config/routes';

export type NavPage = {
  path: string;
  /** i18n key of the label */
  labelKey: string;
  /** i18n key of a one line description, shown in the quick find */
  descriptionKey: string;
  /** i18n key of a short summary, shown under the label in the menu */
  summaryKey: string;
  /** i18n key of other words the quick find finds it by */
  keywordsKey?: string;
  /** Only shown to project members when 'projectMember' */
  requiredAccessLevel?: 'authenticated' | 'projectMember';
};

export type NavSection = {
  /** i18n key of the menu label */
  labelKey: string;
  /** The pages of the section, in tab order */
  pages: NavPage[];
};

/** The menu items for signed-in users, in the order they are shown. */
const mainNavigation: NavSection[] = [
  {
    labelKey: 'common.data',
    pages: [
      { path: routes.files, labelKey: 'files.title', descriptionKey: 'files.description', summaryKey: 'navigation.files' },
      { path: routes.datasets, labelKey: 'datasets.title', descriptionKey: 'datasets.description', summaryKey: 'navigation.datasets' },
      { path: routes.keyFigures, labelKey: 'keyFigures.title', descriptionKey: 'keyFigures.description', summaryKey: 'navigation.keyFigures' },
      { path: routes.tpsMapping, labelKey: 'tpsMapping.title', descriptionKey: 'tpsMapping.description', summaryKey: 'navigation.tpsMapping' },
    ],
  },
  {
    labelKey: 'tools.title',
    pages: [
      { path: routes.search, labelKey: 'search.title', descriptionKey: 'search.description', summaryKey: 'navigation.search' },
      { path: routes.tools, labelKey: 'tools.allTools', descriptionKey: 'tools.description', summaryKey: 'navigation.tools' },
      // The data quality portal is hidden until it is released
      // { path: routes.dataQualityPortal, labelKey: 'tools.validationPortal', descriptionKey: 'tools.validationPortalDesc', summaryKey: 'navigation.dataQualityPortal', keywordsKey: 'tools.validationPortalKeywords', requiredAccessLevel: 'projectMember' },
      // { path: routes.map, labelKey: 'map.title', descriptionKey: 'map.description' },  // Map section is still under development
    ],
  },
  {
    labelKey: 'catalog.title',
    pages: [
      { path: routes.catalog, labelKey: 'catalog.title', descriptionKey: 'catalog.description', summaryKey: 'navigation.catalog' },
    ],
  },
  {
    labelKey: 'docs.title',
    pages: [
      { path: routes.swagger, labelKey: 'swagger.title', descriptionKey: 'swagger.intro', summaryKey: 'navigation.swagger' },
      { path: routes.statusReports, labelKey: 'statusReports.title', descriptionKey: 'statusReports.description', summaryKey: 'navigation.statusReports' },
      { path: routes.rinfStatus, labelKey: 'rinfStatus.title', descriptionKey: 'rinfStatus.description', summaryKey: 'navigation.rinfStatus' },
      { path: routes.railmlStatus, labelKey: 'railmlStatus.title', descriptionKey: 'railmlStatus.description', summaryKey: 'navigation.railmlStatus' },
    ],
  },
];

/** Whether the pathname is the page itself or one of its sub pages, e.g. /reports/status/2026-09. */
export const isOnPage = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

/** The pages of the section the user may see. */
export const visiblePages = (section: NavSection, isProjectMember: boolean): NavPage[] =>
  section.pages.filter((page) => page.requiredAccessLevel !== 'projectMember' || isProjectMember);

/** The menu sections with only the pages the user may see, leaving out sections without any. */
export const visibleNavigation = (isProjectMember: boolean): NavSection[] =>
  mainNavigation
    .map((section) => ({ ...section, pages: visiblePages(section, isProjectMember) }))
    .filter((section) => section.pages.length > 0);

/** Whether the pathname belongs to one of the pages of the section. */
export const isInSection = (pathname: string, section: NavSection): boolean =>
  section.pages.some((page) => isOnPage(pathname, page.path));

export default mainNavigation;
